import {
  Box,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import ImageLightbox from 'components/ImageLightbox/ImageLightbox';
import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import imgRegister1 from 'assets/guide/register-1.png';
import imgRegister2 from 'assets/guide/register-2.png';
import imgRegister3 from 'assets/guide/register-3.jpg';
import imgRegister4 from 'assets/guide/register-4.jpg';

const HEADER_OFFSET = 96;
const BOT_URL = 'https://t.me/rent_system24_bot';

const stepsData = [
  {
    num: 1,
    titleKey: 'landing.accountGuide.step1.title',
    descKey: 'landing.accountGuide.step1.desc',
    images: [{ src: imgRegister1, altKey: 'landing.accountGuide.step1.title' }]
  },
  {
    num: 2,
    titleKey: 'landing.accountGuide.step2.title',
    descKey: 'landing.accountGuide.step2.desc',
    images: [
      { src: imgRegister2, altKey: 'landing.accountGuide.step2.title' },
      { src: imgRegister3, altKey: 'landing.accountGuide.step2.codeAlt' }
    ]
  },
  {
    num: 3,
    titleKey: 'landing.accountGuide.step3.title',
    descKey: 'landing.accountGuide.step3.desc',
    images: [{ src: imgRegister4, altKey: 'landing.accountGuide.step3.title' }]
  }
];

const GuideImage = ({ src, alt, onOpen }) => (
  <Box
    role={onOpen ? 'button' : undefined}
    tabIndex={onOpen ? 0 : undefined}
    onClick={onOpen ? () => onOpen(src, alt) : undefined}
    onKeyDown={
      onOpen
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(src, alt);
            }
          }
        : undefined
    }
    sx={{
      width: '100%',
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      cursor: onOpen ? 'zoom-in' : 'default',
      boxShadow: (theme) => `0 4px 12px 0 ${alpha(theme.palette.grey[500], 0.1)}`,
      transition: onOpen ? 'transform 0.15s ease, box-shadow 0.15s ease' : undefined,
      ...(onOpen
        ? {
            '&:hover': {
              boxShadow: (theme) => `0 8px 20px 0 ${alpha(theme.palette.grey[500], 0.2)}`,
              transform: 'translateY(-1px)'
            }
          }
        : {}),
      '& img': {
        width: '100%',
        height: 'auto',
        display: 'block',
        objectFit: 'cover',
        pointerEvents: onOpen ? 'none' : undefined
      }
    }}>
    <img src={src} alt={alt} loading="lazy" />
  </Box>
);

const AccountStep = ({ step, title, description, images = [], isActive, onImageOpen, stackImages }) => {
  const theme = useTheme();

  return (
    <Box
      id={`account-step-${step}`}
      component="section"
      sx={{
        mb: 4,
        display: 'flex',
        alignItems: 'stretch',
        scrollMarginTop: HEADER_OFFSET + 12,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isActive ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        boxShadow: isActive ? theme.shadows[6] : theme.shadows[2],
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
      }}>
      <Box
        sx={{
          width: { xs: 48, sm: 72 },
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: isActive ? alpha(theme.palette.primary.main, 0.06) : 'background.neutral'
        }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            mx: 'auto',
            mt: 2.5,
            mb: 2.5,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 15,
            boxShadow: theme.shadows[4]
          }}>
          {step}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <Typography
          variant="h6"
          fontWeight={700}
          color={isActive ? 'primary.main' : 'text.primary'}
          sx={{ mb: 1.5 }}>
          {title}
        </Typography>

        {description}

        {images.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              mt: 2,
              gridTemplateColumns: stackImages
                ? '1fr'
                : {
                    xs: '1fr',
                    md: images.length > 1 ? '1fr 1fr' : '1fr'
                  }
            }}>
            {images.map((img) => (
              <GuideImage
                key={img.src}
                src={img.src}
                alt={img.alt}
                onOpen={onImageOpen}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default function LandingAccountGuideSection() {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(1);
  const [lightbox, setLightbox] = useState({ open: false, url: '', name: '' });

  const handleImageOpen = (url, name) => {
    setLightbox({ open: true, url, name: name || 'guide' });
  };

  const handleLightboxClose = () => {
    setLightbox({ open: false, url: '', name: '' });
  };

  const handleDownloadImage = () => {
    if (!lightbox.url) return;
    const a = document.createElement('a');
    a.href = lightbox.url;
    a.download = `${lightbox.name || 'guide'}.jpg`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll('[id^="account-step-"]'));
    if (!elements.length) {
      return undefined;
    }

    const updateActiveStep = () => {
      const marker = HEADER_OFFSET + 24;
      const passed = elements.filter((el) => el.getBoundingClientRect().top <= marker);
      const current = passed[passed.length - 1] || elements[0];
      const stepId = Number(current.id.split('-').pop());
      if (!Number.isNaN(stepId)) {
        setActiveStep(stepId);
      }
    };

    updateActiveStep();
    window.addEventListener('scroll', updateActiveStep, { passive: true });
    return () => window.removeEventListener('scroll', updateActiveStep);
  }, []);

  const scrollToStep = (num) => {
    const el = document.getElementById(`account-step-${num}`);
    if (!el) return;
    setActiveStep(num);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderDescription = (stepNum) => {
    if (stepNum === 2) {
      return (
        <Typography variant="body1" color="text.secondary" paragraph>
          <Trans
            i18nKey="landing.accountGuide.step2.desc"
            components={{
              bot: (
                <Link
                  href={BOT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  fontWeight={600}
                />
              )
            }}
          />
        </Typography>
      );
    }

    return (
      <Typography variant="body1" color="text.secondary" paragraph>
        {t(`landing.accountGuide.step${stepNum}.desc`)}
      </Typography>
    );
  };

  return (
    <Box id="account-guide" sx={{ scrollMarginTop: HEADER_OFFSET, mb: 6 }}>
      <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
        {t('landing.accountGuide.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 800 }}>
        {t('landing.accountGuide.subtitle')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3
        }}>
        <Box sx={{ width: { xs: '100%', md: 'calc(75% - 12px)' }, minWidth: 0 }}>
          {stepsData.map((s) => (
            <AccountStep
              key={s.num}
              step={s.num}
              title={t(s.titleKey)}
              description={renderDescription(s.num)}
              images={s.images.map((img) => ({
                src: img.src,
                alt: t(img.altKey)
              }))}
              isActive={activeStep === s.num}
              stackImages={s.num === 2}
              onImageOpen={s.num === 2 ? handleImageOpen : undefined}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            width: 'calc(25% - 12px)',
            flexShrink: 0,
            position: 'sticky',
            top: HEADER_OFFSET,
            alignSelf: 'flex-start',
            maxHeight: `calc(100vh - ${HEADER_OFFSET + 24}px)`,
            overflowY: 'auto',
            pr: 0.5
          }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {t('landing.accountGuide.toc')}
          </Typography>
          <List disablePadding>
            {stepsData.map((s) => {
              const isActive = activeStep === s.num;
              return (
                <ListItem disablePadding key={s.num} sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => scrollToStep(s.num)}
                    sx={{
                      borderRadius: 1,
                      py: 0.75,
                      transition: 'all 0.2s',
                      bgcolor: isActive ? 'primary.main' : 'transparent',
                      color: isActive ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        bgcolor: isActive ? 'primary.dark' : 'primary.lighter',
                        color: isActive ? 'primary.contrastText' : 'primary.main'
                      }
                    }}>
                    <ListItemText
                      primary={`${s.num}. ${t(s.titleKey)}`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isActive ? 600 : 500,
                        sx: { lineHeight: 1.3 }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Box>

      <ImageLightbox
        open={lightbox.open}
        url={lightbox.url}
        handleClose={handleLightboxClose}
        download={handleDownloadImage}
        imageName={lightbox.name}
      />
    </Box>
  );
}
